import * as XLSX from 'xlsx';
import moment from 'moment';
import DebtService from '../../services/debt.service';
import CreditService from '../../services/credit.service';
import ContractService from '../../services/contract.service';
import AssetService from '../../services/asset.service';
import AssetSaleService from '../../services/assetSale.service';
import PaymentService from '../../services/payment.service';
import GeneratedDebtService from '../../services/generatedDebts.service';
import GeneratedCreditService from '../../services/generatedCredits.service';
import {
	contractsColumns,
	creditsColumns,
	assetsColumns,
} from '../../utils';
import { getLabel } from '../../utils/labelStore';
import { buildReportExportRows } from './reportColumnAdapter';
import { buildReportColumns } from './reportColumns';

/**
 * Per-formType config for the download-only reports: which columnInfo
 * (from utils.js) drives the xlsx sheet, the `dataName` the currency
 * formatter keys off (see reportColumnAdapter.js), and the sheet/label text.
 * Deudores, Cobros, Ventas and the two 'Cuotas y vencimientos' formTypes use
 * `buildColumns` instead of a static `columnInfo`, since their headline amount
 * splits into USD/UYU columns depending on the currency filter. The rest use
 * `currencyOf`, which moves the currency into its own column so amounts are
 * exported as plain numbers.
 */
const ownCurrency = (item) => item.currency;

export const REPORT_EXPORT_CONFIG = {
	Deudas: {
		dataName: 'debts',
		buildColumns: (filterOptions) => buildReportColumns('Deudas', filterOptions),
		label: 'Deudores',
	},
	Pasividades: { dataName: 'credits', columnInfo: creditsColumns, label: 'Pasividades', currencyOf: ownCurrency },
	Alquileres: { dataName: 'contracts', columnInfo: contractsColumns, label: 'Alquileres', currencyOf: ownCurrency },
	ActivosFijos: { dataName: 'assets', columnInfo: assetsColumns, label: 'Activos Fijos', currencyOf: ownCurrency },
	VentasActivo: {
		dataName: 'assetSales',
		buildColumns: (filterOptions) => buildReportColumns('VentasActivo', filterOptions),
		label: 'Ventas',
	},
	Cobros: {
		dataName: 'payments',
		buildColumns: (filterOptions) => buildReportColumns('Cobros', filterOptions),
		label: 'Cobros',
	},
	CuotasDeudas: {
		dataName: 'debtsDues',
		buildColumns: (filterOptions) => buildReportColumns('CuotasDeudas', filterOptions),
		label: 'Cuotas de deudas',
	},
	CuotasCreditos: {
		dataName: 'creditDues',
		buildColumns: (filterOptions) => buildReportColumns('CuotasCreditos', filterOptions),
		label: 'Cuotas de pasividades',
	},
};

// When sold assets are included, a Vendido? column tells them apart.
const soldColumn = { sold: { type: 'boolean', get label() { return getLabel('field.sold'); } } };

export function getReportColumns(filterOptions) {
	const config = REPORT_EXPORT_CONFIG[filterOptions.formType];
	const columnInfo = config.buildColumns ? config.buildColumns(filterOptions) : config.columnInfo;
	if (filterOptions.formType === 'ActivosFijos' && filterOptions.includeSold) return [...columnInfo, soldColumn];
	return columnInfo;
}

function slugify(value) {
	return value
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

/**
 * Lifted verbatim (same keys, same `|| ''` defaults, same `report=true`
 * flags) from the old getFilterUrl per-formType blocks — minus the leading
 * route path and page/reportId params, which don't apply to an in-memory
 * fetch. The two fixed-period reports (`report.hasFilters === false`)
 * reproduce the fetch effect's month override exactly.
 */
export function buildReportParams(report, filterOptions) {
	const {
		concept, query, periodInitFrom, periodInitTo, periodEndFrom, periodEndTo,
		dateFrom, dateTo, lastPaymentFrom, lastPaymentTo, endDateFrom, endDateTo,
		amountFrom, amountTo, capitalFrom, capitalTo, currency, paid,
		priceFrom, priceTo, name, lot, ubication, owner, purchaser, includeSold,
	} = filterOptions;

	const params = new URLSearchParams();

	switch (filterOptions.formType) {
	case 'Deudas':
	case 'Pasividades':
		params.append('page', 1);
		params.append('concept', concept || '');
		params.append('query', query || '');
		params.append('dateFrom', dateFrom || '');
		params.append('dateTo', dateTo || '');
		params.append('endDateFrom', endDateFrom || '');
		params.append('endDateTo', endDateTo || '');
		params.append('amountFrom', amountFrom || '');
		params.append('amountTo', amountTo || '');
		params.append('capitalFrom', capitalFrom || '');
		params.append('capitalTo', capitalTo || '');
		params.append('currency', currency || '');
		params.append('report', 'true');
		break;

	case 'Alquileres':
		params.append('amountFrom', amountFrom || '');
		params.append('amountTo', amountTo || '');
		params.append('periodInitFrom', periodInitFrom || '');
		params.append('periodInitTo', periodInitTo || '');
		params.append('periodEndFrom', periodEndFrom || '');
		params.append('periodEndTo', periodEndTo || '');
		params.append('currency', currency || '');
		params.append('query', query || '');
		params.append('page', 1);
		params.append('report', 'true');
		break;

	case 'ActivosFijos':
		params.append('priceFrom', priceFrom || '');
		params.append('priceTo', priceTo || '');
		params.append('currency', currency || '');
		params.append('name', name || '');
		params.append('ubication', ubication || '');
		params.append('lot', lot || '');
		// Empty `sold` returns every asset; 'no' keeps only the unsold ones.
		params.append('sold', includeSold ? '' : 'no');
		params.append('owner', owner || '');
		params.append('page', 1);
		break;

	case 'VentasActivo':
		params.append('dateFrom', dateFrom || '');
		params.append('dateTo', dateTo || '');
		params.append('currency', currency || '');
		params.append('purchaser', purchaser || '');
		params.append('lot', lot || '');
		params.append('page', 1);
		params.append('report', 'true');
		break;

	case 'Cobros':
		params.append('page', 1);
		params.append('concept', concept || '');
		params.append('dateFrom', dateFrom || '');
		params.append('dateTo', dateTo || '');
		params.append('currency', currency || '');
		params.append('report', 'true');
		break;

	case 'CuotasDeudas':
		if (report.hasFilters === false) {
			const currentDate = moment.utc();
			params.append('paid', 'true');
			params.append('concept', concept || '');
			params.append('dateFrom', currentDate.clone().startOf('month').format('YYYY-MM-DD'));
			params.append('dateTo', currentDate.clone().endOf('month').format('YYYY-MM-DD'));
			params.append('page', 1);
			params.append('report', 'true');
		} else {
			params.append('concept', concept || '');
			params.append('dateFrom', dateFrom || '');
			params.append('dateTo', dateTo || '');
			params.append('lastPaymentFrom', lastPaymentFrom || '');
			params.append('lastPaymentTo', lastPaymentTo || '');
			params.append('paid', paid || '');
			params.append('currency', currency || '');
			params.append('query', query || '');
			params.append('page', 1);
			params.append('report', 'true');
		}
		break;

	case 'CuotasCreditos':
		if (report.hasFilters === false) {
			const currentDate = moment.utc();
			params.append('paid', 'true');
			params.append('concept', concept || '');
			params.append('dateFrom', currentDate.clone().startOf('month').format('YYYY-MM-DD'));
			params.append('dateTo', currentDate.clone().endOf('month').format('YYYY-MM-DD'));
			params.append('page', 1);
			params.append('report', 'true');
		} else {
			params.append('concept', concept || '');
			params.append('dateFrom', dateFrom || '');
			params.append('dateTo', dateTo || '');
			params.append('paid', paid || '');
			params.append('currency', currency || '');
			params.append('query', query || '');
			params.append('page', 1);
			params.append('report', 'true');
		}
		break;

	default:
		break;
	}

	return params;
}

/**
 * Fetches the full (unpaginated) dataset for `report` and builds an xlsx
 * blob from it. Debts is the one exception — its route gates the full
 * dataset on `exportData`, not `report`, and returns a params object
 * rather than taking a raw query string.
 */
export async function generateReportFile(report, filterOptions) {
	const config = REPORT_EXPORT_CONFIG[filterOptions.formType];
	const params = buildReportParams(report, filterOptions);
	let rows;

	switch (filterOptions.formType) {
	case 'Deudas': {
		const paramsObject = { ...Object.fromEntries(params.entries()), exportData: 'true' };
		const { data } = await DebtService.searchReport(paramsObject);
		rows = data.allDebts ?? [];
		break;
	}
	case 'Pasividades': {
		const { data } = await CreditService.search(params.toString());
		rows = data.allCredits ?? [];
		break;
	}
	case 'Alquileres': {
		const { data } = await ContractService.search(params.toString());
		rows = data.allContracts ?? [];
		break;
	}
	case 'ActivosFijos': {
		const { data } = await AssetService.search(params.toString());
		rows = data.allAssets ?? [];
		break;
	}
	case 'VentasActivo': {
		const { data } = await AssetSaleService.search(params.toString());
		rows = data.allAssetSales ?? [];
		break;
	}
	case 'Cobros': {
		const { data } = await PaymentService.search(params.toString());
		rows = data.allPayments ?? [];
		break;
	}
	case 'CuotasDeudas': {
		const { data } = await GeneratedDebtService.search(params.toString());
		rows = data.allDebtsDues ?? [];
		break;
	}
	case 'CuotasCreditos': {
		const { data } = await GeneratedCreditService.search(params.toString());
		rows = data.allCreditsDues ?? [];
		break;
	}
	default:
		rows = [];
	}

	const exportRows = buildReportExportRows(getReportColumns(filterOptions), config.dataName, rows, {
		currencyOf: config.currencyOf,
	});
	const ws = XLSX.utils.json_to_sheet(exportRows);
	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, config.label);
	const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
	const blob = new Blob([buffer], { type: 'application/octet-stream' });
	const filename = `${slugify(report.id)}-${moment.utc().format('YYYY-MM-DD')}.xlsx`;
	return { blob, filename, count: rows.length };
}
